/* ═══════════════════════════════════════════════════════════
   ACTIVATION TRACKER — Track premium feature usage during trial
   Persists in localStorage. Drives smart paywall decisions.
   ═══════════════════════════════════════════════════════════ */

const STORAGE_KEY = "sl_feature_activation";

const ALL_FEATURES = [
  "ai-unlimited", "quiz-modes", "session-recordings", "notes-summarizer",
  "advanced-analytics", "premium-rooms", "priority-booking", "advanced-filters",
];

const DEFAULT_DATA = {
  trialStartedAt: null,
  trialExpiredAt: null,
  features: {},
  teaserUsed: {},
};

/* ── Persistence ─────────────────────────────────────────── */

function load() {
  try {
    return { ...DEFAULT_DATA, ...JSON.parse(localStorage.getItem(STORAGE_KEY)) };
  } catch {
    return { ...DEFAULT_DATA };
  }
}

function save(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

/* ── Public API ──────────────────────────────────────────── */

/** Initialize tracker at signup (called after trial subscription is created) */
export function initActivation(trialStartedAt) {
  save({
    trialStartedAt,
    trialExpiredAt: null,
    features: {},
    teaserUsed: {},
  });
}

/** Record that a premium feature was used during the trial */
export function trackFeatureUse(featureId, snapshotData = {}) {
  const data = load();
  const now = new Date().toISOString();

  if (!data.features[featureId]) {
    data.features[featureId] = {
      firstUsed: now,
      lastUsed: now,
      useCount: 1,
      snapshot: {},
    };
  } else {
    data.features[featureId].lastUsed = now;
    data.features[featureId].useCount += 1;
  }

  // Merge snapshot with auto-collected data
  const autoSnapshot = collectSnapshot(featureId);
  data.features[featureId].snapshot = { ...autoSnapshot, ...snapshotData };

  save(data);
  return data.features[featureId];
}

/** Get the full activation object */
export function getActivation() {
  return load();
}

/** Check if a specific feature was used during the trial */
export function wasFeatureUsed(featureId) {
  const data = load();
  return (data.features[featureId]?.useCount || 0) > 0;
}

/** Get the snapshot data for a feature (for loss-aversion messaging) */
export function getFeatureSnapshot(featureId) {
  const data = load();
  return data.features[featureId]?.snapshot || null;
}

/** Get usage count for a feature */
export function getFeatureUseCount(featureId) {
  const data = load();
  return data.features[featureId]?.useCount || 0;
}

/** Mark the trial as expired (called when PremiumContext detects expiry) */
export function markTrialExpired(expiredAt) {
  const data = load();
  if (!data.trialExpiredAt) {
    data.trialExpiredAt = expiredAt;
    save(data);
  }
}

/** Record a teaser use for an unused feature (post-trial, 1 free try) */
export function recordTeaserUse(featureId) {
  const data = load();
  data.teaserUsed[featureId] = (data.teaserUsed[featureId] || 0) + 1;
  save(data);
  return data.teaserUsed[featureId];
}

/** Get teaser use count for a feature */
export function getTeaserCount(featureId) {
  const data = load();
  return data.teaserUsed[featureId] || 0;
}

/** Reset tracker (called on new subscription) */
export function resetActivation() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/** Get loss-aversion message data for a specific feature */
export function getLossAversionData(featureId) {
  const data = load();
  const activation = data.features[featureId];
  if (!activation || activation.useCount === 0) return null;

  const s = activation.snapshot || {};
  const useCount = activation.useCount;

  switch (featureId) {
    case "ai-unlimited":
      return {
        headline: `You asked ${s.questionsAsked || useCount} questions`,
        detail: s.streak ? `${s.streak}-day AI streak in ${s.topSubject || "your subjects"}` : null,
        loss: "Your AI chat history and learning profile will be limited to 5 questions/day.",
        cta: "Keep My AI Access",
      };
    case "quiz-modes":
      return {
        headline: `You completed ${(s.timedQuizzes || 0) + (s.practiceQuizzes || 0)} advanced quizzes`,
        detail: s.bestScore ? `Best score: ${s.bestScore}%` : s.quizStreak ? `${s.quizStreak}-day streak` : null,
        loss: "Timed Exams and Practice Mode will be locked. Your streak is at risk.",
        cta: "Keep My Quiz Modes",
      };
    case "session-recordings":
      return {
        headline: `You viewed ${s.recordingsViewed || useCount} session recordings`,
        detail: null,
        loss: "You won't be able to replay past tutoring sessions.",
        cta: "Keep My Recordings",
      };
    case "premium-rooms":
      return {
        headline: `You created ${s.roomCount || useCount} premium study rooms`,
        detail: s.maxSeats ? `With ${s.maxSeats}-seat capacity` : null,
        loss: "Room limit drops to 3 and seat capacity to 4.",
        cta: "Keep My Study Rooms",
      };
    case "advanced-analytics":
      return {
        headline: "You've been tracking your study progress",
        detail: s.subjectCount ? `${s.subjectCount} subjects analyzed` : null,
        loss: "Detailed analytics and improvement trends will be locked.",
        cta: "Keep My Analytics",
      };
    case "notes-summarizer":
      return {
        headline: `You summarized ${s.notesCount || useCount} sets of notes`,
        detail: null,
        loss: "AI note summarization will no longer be available.",
        cta: "Keep My Summarizer",
      };
    case "priority-booking":
      return {
        headline: "You used priority tutor booking",
        detail: s.bookingsCount ? `${s.bookingsCount} priority bookings made` : null,
        loss: "You'll return to the standard booking queue.",
        cta: "Keep Priority Access",
      };
    case "advanced-filters":
      return {
        headline: "You used advanced tutor search filters",
        detail: null,
        loss: "Filter by success rate & response time will be locked.",
        cta: "Keep Advanced Filters",
      };
    default:
      return {
        headline: `You used this feature ${useCount} times`,
        detail: null,
        loss: "This feature will be locked.",
        cta: "Keep This Feature",
      };
  }
}

/* ── Auto-Snapshot Collection ────────────────────────────── */

function collectSnapshot(featureId) {
  switch (featureId) {
    case "ai-unlimited":
      return collectAISnapshot();
    case "quiz-modes":
      return collectQuizSnapshot();
    default:
      return {};
  }
}

function collectAISnapshot() {
  try {
    const mem = JSON.parse(localStorage.getItem("sl_ai_memory") || "{}");
    const topicsAsked = mem.topicsAsked || {};
    const topSubject = Object.entries(topicsAsked).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    return {
      questionsAsked: mem.questionsAsked || 0,
      streak: mem.streak || 0,
      topSubject,
    };
  } catch {
    return {};
  }
}

function collectQuizSnapshot() {
  try {
    const analytics = JSON.parse(localStorage.getItem("sl_quiz_analytics") || "{}");
    const results = analytics.results || [];
    const timedQuizzes = results.filter(r => r.mode === "timed").length;
    const practiceQuizzes = results.filter(r => r.mode === "practice").length;
    const timedScores = results.filter(r => r.mode === "timed").map(r => r.pct || 0);
    const bestScore = timedScores.length > 0 ? Math.max(...timedScores) : 0;
    return {
      timedQuizzes,
      practiceQuizzes,
      bestScore,
      quizStreak: analytics.streak || 0,
    };
  } catch {
    return {};
  }
}
